import React, { useState, useEffect } from "react";
import { Database, Table, Loader } from "lucide-react";
import ReactFlow, {
  Background,
  Controls,
  MiniMap,
  useNodesState,
  useEdgesState,
  Handle,
  Position,
} from "reactflow";
import "reactflow/dist/style.css";
import { ChevronDown, ChevronRight } from "lucide-react";
import axiosClient from "../../../../api-client/AxiosClient";

const API_BASE_URL = "https://apierd.bluegecko.ai:8004";

// Add custom scrollbar styles
const scrollbarStyles = `
  .scrollbar-thin::-webkit-scrollbar {
    width: 6px;
  }
  .scrollbar-thin::-webkit-scrollbar-track {
    background: transparent;
  }
  .scrollbar-thin::-webkit-scrollbar-thumb {
    background: #d1d5db;
    border-radius: 3px;
  }
  .scrollbar-thin::-webkit-scrollbar-thumb:hover {
    background: #9ca3af;
  }
`;

// Inject styles
if (typeof document !== "undefined") {
  const style = document.createElement("style");
  style.textContent = scrollbarStyles;
  document.head.appendChild(style);
}

const DataLineageDashboard = () => {
  const [dataSources, setDataSources] = useState([]);
  const [selectedDataSource, setSelectedDataSource] = useState(null);
  const [expandedSource, setExpandedSource] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedTable, setSelectedTable] = useState(null);
  const [viewMode, setViewMode] = useState("graph");
  const [tables, setTables] = useState([]);
  const [tableDetails, setTableDetails] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingDataSources, setLoadingDataSources] = useState(true);
  const [dataSourceError, setDataSourceError] = useState(null);

  useEffect(() => {
    fetchDataSources();
  }, []);

  const fetchDataSources = async () => {
    setLoadingDataSources(true);
    setDataSourceError(null);

    try {
      // Get corporation ID from localStorage
      const storedCorporation = localStorage.getItem("selectedCorporation");

      if (!storedCorporation) {
        setDataSourceError("No corporation selected");
        setDataSources([]);
        setLoadingDataSources(false);
        return;
      }

      const corporation = JSON.parse(storedCorporation);
      const corporationId = corporation.value || corporation.corporationid;

      if (!corporationId) {
        setDataSourceError("Invalid corporation data");
        setDataSources([]);
        setLoadingDataSources(false);
        return;
      }

      // Fetch data sources from API
      const response = await axiosClient.get(
        `/api/MappingDB/CorpMappingDBByUserCorp?corporationID=${corporationId}`
      );

      if (response && response.data) {
        const data = Array.isArray(response.data) ? response.data : [];

        // Map API data to component format
        const mappedDataSources = data.map((ds) => ({
          ...ds,
          apiSource: ds.name, // Use name as apiSource for API calls
        }));

        setDataSources(mappedDataSources);
      } else {
        setDataSources([]);
      }
    } catch (err) {
      console.error("Error fetching data sources:", err);
      setDataSourceError(err.message || "Failed to fetch data sources");
      setDataSources([]);
    } finally {
      setLoadingDataSources(false);
    }
  };

  // Listen for corporation changes
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (
        e.key === "selectedCorporation" ||
        e.detail?.key === "selectedCorporation"
      ) {
        fetchDataSources();
      }
    };

    const handleCustomStorageChange = (e) => {
      if (e.detail?.key === "selectedCorporation") {
        fetchDataSources();
      }
    };

    window.addEventListener("storage", handleStorageChange);
    window.addEventListener("customStorageChange", handleCustomStorageChange);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
      window.removeEventListener(
        "customStorageChange",
        handleCustomStorageChange
      );
    };
  }, []);

  const fetchTables = async (dataSource) => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(
        `${API_BASE_URL}/tables?source=${dataSource.apiSource}`
      );
      if (!response.ok) throw new Error("Failed to fetch tables");
      const data = await response.json();
      setTables(data.tables || []);
    } catch (err) {
      setError(err.message);
      setTables([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchTableDetails = async (dataSource, tableName) => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(
        `${API_BASE_URL}/table-data?source=${dataSource.apiSource}&table_name=${tableName}`
      );
      if (!response.ok) throw new Error("Failed to fetch table details");
      const data = await response.json();
      setTableDetails(data);
    } catch (err) {
      setError(err.message);
      setTableDetails(null);
    } finally {
      setLoading(false);
    }
  };

  const handleDataSourceSelect = (dataSource) => {
    const isExpanding = expandedSource !== dataSource.corpmappingdbid;

    setExpandedSource(isExpanding ? dataSource.corpmappingdbid : null);
    setSelectedDataSource(isExpanding ? dataSource : null);

    setSelectedTable(null);
    setTableDetails(null);

    if (isExpanding) {
      fetchTables(dataSource);
    }
  };

  const handleTableSelect = (table) => {
    setSelectedTable(table);
    fetchTableDetails(selectedDataSource, table.table_name);
  };

  // const getDBIcon = (dbType) => {
  //   const type = dbType?.toLowerCase() || "";
  //   if (type.includes("mssql") || type.includes("sql")) return "🗄️";
  //   if (type.includes("db2")) return "🐘";
  //   return "📊";
  // };

  const filteredTables = tables.filter((table) =>
    table.table_name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="w-full h-screen flex bg-gray-50 relative overflow-hidden">
      {/* Header */}
      <div className="absolute top-0 left-0 right-80 bg-white border-b border-gray-200 px-6 py-2 flex items-center justify-between h-20 z-30 pr-100">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-blue-100 rounded-lg">
            <Database className="w-5 h-5 text-blue-600" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-gray-900">ERD</h2>
            <p className="text-sm text-gray-500">
              Visualize table structures and relationships
            </p>
          </div>
        </div>

        {selectedTable && (
          <div className="flex items-center gap-3 bg-blue-50 px-4 py-2 rounded-lg border border-blue-200">
            <Table className="w-4 h-4 text-blue-600 flex-shrink-0" />
            <span className="text-sm font-semibold text-gray-900 truncate">
              {selectedTable.table_name}
            </span>
            <div className="flex gap-2 ml-3 border-l border-blue-200 pl-3">
              <button
                onClick={() => setViewMode("details")}
                className={`px-3 py-1.5 text-xs font-medium rounded transition-colors ${
                  viewMode === "details"
                    ? "bg-blue-600 text-white"
                    : "bg-white text-gray-700 hover:bg-gray-100"
                }`}
              >
                Details
              </button>
              <button
                onClick={() => setViewMode("graph")}
                className={`px-3 py-1.5 text-xs font-medium rounded transition-colors ${
                  viewMode === "graph"
                    ? "bg-blue-600 text-white"
                    : "bg-white text-gray-700 hover:bg-gray-100"
                }`}
              >
                Graph
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Main Content */}
      <div className="w-full h-full flex flex-row-reverse overflow-hidden">
        {/* Right Sidebar */}
        <div className="w-80 bg-white border-l border-gray-200 flex flex-col overflow-hidden flex-shrink-0">
          <div className="px-2 py-3 border-gray-200 flex-shrink-0 bg-white sticky top-0 z-30">
            <div className="relative">
              <input
                type="text"
                placeholder="Search tables..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-3 py-2.5 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm bg-white placeholder-gray-500 shadow-sm"
              />
              <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400">
                🔍
              </span>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto scrollbar-thin pb-4">
            <div className="p-3 space-y-2">
              {loadingDataSources ? (
                <div className="flex items-center justify-center py-8">
                  <Loader className="w-6 h-6 animate-spin text-blue-600" />
                  <span className="ml-2 text-sm text-gray-600">
                    Loading data sources...
                  </span>
                </div>
              ) : dataSourceError ? (
                <div className="px-3 py-4 text-center">
                  <p className="text-sm text-red-600 mb-2">{dataSourceError}</p>
                  <button
                    onClick={fetchDataSources}
                    className="text-xs text-blue-600 hover:text-blue-800 font-medium"
                  >
                    Retry
                  </button>
                </div>
              ) : dataSources.length === 0 ? (
                <div className="px-3 py-8 text-center">
                  <Database className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                  <p className="text-sm text-gray-600">No data sources found</p>
                  <p className="text-xs text-gray-500 mt-1">
                    Please add a data source first
                  </p>
                </div>
              ) : (
                dataSources.map((ds) => (
                  <div key={ds.corpmappingdbid}>
                    <button
                      onClick={() => handleDataSourceSelect(ds)}
                      className={`w-full px-3 py-2.5 text-left rounded-lg transition-colors text-sm mb-1 ${
                        selectedDataSource?.corpmappingdbid ===
                        ds.corpmappingdbid
                          ? "bg-blue-50 border border-blue-300"
                          : "hover:bg-gray-50 border border-transparent"
                      }`}
                    >
                      <div className="flex items-center justify-between gap-3">
                        <div className="flex items-center gap-2 flex-1 min-w-0">
                          <div className="min-w-0">
                            <h3 className="font-semibold text-gray-900 text-sm truncate">
                              {ds.name}
                            </h3>
                            <p className="text-xs text-gray-500 mt-0.5">
                              {ds.mappingdbtype}
                            </p>
                          </div>
                        </div>

                        {expandedSource === ds.corpmappingdbid ? (
                          <ChevronDown className="w-4 h-4 text-gray-600" />
                        ) : (
                          <ChevronRight className="w-4 h-4 text-gray-600" />
                        )}

                        <span
                          className={`inline-block w-2 h-2 rounded-full ${
                            ds.isactive ? "bg-green-500" : "bg-red-500"
                          }`}
                        ></span>
                      </div>
                    </button>

                    {selectedDataSource?.corpmappingdbid ===
                      ds.corpmappingdbid && (
                      <div className="ml-2 mb-3 border-l-2 border-blue-300 pl-3 space-y-1">
                        {loading ? (
                          <div className="px-3 py-2 text-xs text-gray-500 flex items-center gap-2">
                            <Loader className="w-3 h-3 animate-spin" />{" "}
                            Loading...
                          </div>
                        ) : error ? (
                          <div className="px-3 py-2 text-xs text-red-500 bg-red-50 rounded">
                            {error}
                          </div>
                        ) : filteredTables.length === 0 ? (
                          <div className="px-3 py-2 text-xs text-gray-500 text-center">
                            No tables
                          </div>
                        ) : (
                          filteredTables.map((table) => (
                            <button
                              key={table.table_name}
                              onClick={() => handleTableSelect(table)}
                              className={`w-full px-3 py-2.5 text-left rounded-lg transition-all duration-200 text-xs ${
                                selectedTable?.table_name === table.table_name
                                  ? "bg-gradient-to-r from-purple-100 to-purple-50 text-purple-900 border border-purple-300 font-semibold shadow-sm"
                                  : "text-gray-700 hover:bg-gray-100 border border-transparent hover:border-gray-300"
                              }`}
                            >
                              <div className="flex items-start gap-2">
                                <span className="mt-0.5 flex-shrink-0">📋</span>
                                <div className="flex-1 min-w-0">
                                  <p className="font-medium truncate">
                                    {table.table_name}
                                  </p>
                                  <p className="text-gray-500 text-xs mt-0.5">
                                    Col: {table.column_count}
                                  </p>
                                </div>
                              </div>
                            </button>
                          ))
                        )}
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
        {/* Main Content Area */}
        <div className="flex-1 flex flex-col bg-white min-w-0 pt-20 overflow-y-auto scrollbar-thin h-[calc(100vh-80px)]">
          {!selectedTable ? (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center">
                <div className="text-6xl mb-4">📊</div>
                <p className="text-gray-600 text-lg font-semibold mb-2">
                  Select a table to view details
                </p>
                <p className="text-gray-400 text-sm">
                  Choose a data source and table from the left panel
                </p>
              </div>
            </div>
          ) : loading ? (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center">
                <Loader className="w-8 h-8 animate-spin text-blue-600 mx-auto mb-3" />
                <p className="text-gray-600">Loading table details...</p>
              </div>
            </div>
          ) : error ? (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center">
                <p className="text-red-600 mb-2">Error loading table details</p>
                <p className="text-gray-600 text-sm">{error}</p>
              </div>
            </div>
          ) : viewMode === "details" ? (
            <DetailsView tableDetails={tableDetails} />
          ) : (
            <GraphView tableDetails={tableDetails} />
          )}
        </div>
      </div>
    </div>
  );
};

// Details View Component
const DetailsView = ({ tableDetails }) => {
  if (!tableDetails) return null;

  const getKeyTypeColor = (keyType) => {
    if (!keyType || keyType === "None") return "bg-gray-100 text-gray-700";
    if (keyType === "Primary") return "bg-blue-100 text-blue-700";
    if (keyType === "Foreign") return "bg-purple-100 text-purple-700";
    return "bg-gray-100 text-gray-700";
  };

  return (
    <div className="flex-1 overflow-y-auto min-h-0">
      <div className="p-6 space-y-6">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-purple-100 rounded-lg">
            <Table className="w-5 h-5 text-purple-600" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-gray-900">
              {tableDetails.table_name}
            </h2>
            <p className="text-gray-500 text-sm mt-1">
              Data Source: {tableDetails.data_source}
            </p>
          </div>
        </div>

        <div className="grid grid-cols-3 gap-3">
          <div className="border border-gray-200 bg-gray-50 rounded-md p-3">
            <p className="text-xs text-gray-600">Total Columns</p>
            <p className="text-lg font-semibold text-gray-900">
              {tableDetails.column_count}
            </p>
          </div>

          <div className="border border-gray-200 bg-gray-50 rounded-md p-3">
            <p className="text-xs text-gray-600">Primary Keys</p>
            <p className="text-lg font-semibold text-gray-900">
              {tableDetails.primary_keys?.length || 0}
            </p>
          </div>

          <div className="border border-gray-200 bg-gray-50 rounded-md p-3">
            <p className="text-xs text-gray-600">Overall Confidence</p>
            <p className="text-lg font-semibold text-gray-900">
              {(tableDetails.overall_confidence * 100).toFixed(0)}%
            </p>
          </div>
        </div>

        <div className="space-y-6">
          {tableDetails.references?.length > 0 && (
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">
                Tables Referenced (Upstream)
              </h3>
              <div className="grid grid-cols-2 gap-3">
                {tableDetails.references?.map((ref, idx) => (
                  <div
                    key={idx}
                    className="border border-blue-200 bg-blue-50 rounded-md p-3"
                  >
                    <p className="text-sm font-semibold text-gray-900">
                      {ref.target_table}
                    </p>

                    {ref.column_links?.map((link, i) => (
                      <p key={i} className="text-xs mt-1 text-gray-700">
                        <code className="bg-white px-1 py-0.5 rounded">
                          {link.source_column}
                        </code>{" "}
                        →{" "}
                        <code className="bg-white px-1 py-0.5 rounded">
                          {link.target_column}
                        </code>
                        <span className="ml-1 text-blue-600 font-semibold">
                          {(link.confidence * 100).toFixed(0)}%
                        </span>
                      </p>
                    ))}
                  </div>
                ))}
              </div>
            </div>
          )}

          {tableDetails.referenced_by?.length > 0 && (
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">
                Tables That Reference This (Downstream)
              </h3>

              <div className="grid grid-cols-2 gap-3">
                {tableDetails.referenced_by.map((ref, idx) => (
                  <div
                    key={idx}
                    className="border border-green-200 bg-green-50 rounded-md p-3"
                  >
                    <p className="text-sm font-semibold text-gray-900">
                      {ref.target_table}
                    </p>

                    {ref.column_links?.map((link, i) => (
                      <p key={i} className="text-xs mt-1 text-gray-700">
                        <code className="bg-white px-1 py-0.5 rounded">
                          {link.source_column}
                        </code>{" "}
                        ←{" "}
                        <code className="bg-white px-1 py-0.5 rounded">
                          {link.target_column}
                        </code>
                        <span className="ml-1 text-green-600 font-semibold">
                          {(link.confidence * 100).toFixed(0)}%
                        </span>
                      </p>
                    ))}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="space-y-2">
          <h3 className="text-base font-semibold text-gray-900">
            Table Structure
          </h3>

          <div className="border border-gray-200 rounded-md overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  {["Column", "Type", "Key", "Nullable"].map((t) => (
                    <th
                      key={t}
                      className="px-3 py-2 text-left text-xs font-semibold text-gray-800"
                    >
                      {t}
                    </th>
                  ))}
                </tr>
              </thead>

              <tbody className="divide-y divide-gray-200">
                {tableDetails.table_schema?.map((col, idx) => (
                  <tr key={idx}>
                    <td className="px-3 py-2 font-medium text-gray-900">
                      {col.column_name}
                    </td>
                    <td className="px-3 py-2 text-gray-700">
                      <code className="bg-gray-100 px-2 py-0.5 rounded text-xs">
                        {col.data_type}
                      </code>
                    </td>
                    <td className="px-3 py-2">
                      <span
                        className={`px-2 py-0.5 rounded text-xs ${getKeyTypeColor(
                          col.key_type
                        )}`}
                      >
                        {col.key_type || "None"}
                      </span>
                    </td>
                    <td className="px-3 py-2">
                      <span
                        className={`px-2 py-0.5 rounded text-xs font-medium ${
                          col.nullable === "Yes"
                            ? "bg-yellow-100 text-yellow-700"
                            : "bg-green-100 text-green-700"
                        }`}
                      >
                        {col.nullable}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

// Helper function to split text into two lines for circular nodes (only if needed)
const splitTextForCircle = (text, maxCharsPerLine = 12) => {
  if (!text) return { firstLine: "", secondLine: "" };

  // IMPORTANT: Only split if text is longer than maxCharsPerLine
  // Small words that fit easily in the circle should never be split
  if (text.length <= maxCharsPerLine) {
    return { firstLine: text, secondLine: "" };
  }

  // Only proceed with splitting if text is definitely too long
  let firstLine = "";
  let secondLine = "";

  // Find a good split point - prefer capital letters
  let midPoint = Math.floor(text.length / 2);

  // Find all capital letter positions (skip first character)
  const capitalPositions = [];
  for (let i = 1; i < text.length; i++) {
    if (
      text[i] === text[i].toUpperCase() &&
      text[i] !== text[i].toLowerCase()
    ) {
      capitalPositions.push(i);
    }
  }

  // Try to find a capital letter near the midpoint for balanced split
  let bestSplitIndex = -1;
  let minDistance = Infinity;

  for (const pos of capitalPositions) {
    const distance = Math.abs(pos - midPoint);
    // Only consider splits that are reasonably balanced
    if (distance < minDistance && pos > 0 && pos < text.length) {
      minDistance = distance;
      bestSplitIndex = pos;
    }
  }

  // If we found a good capital letter split point, use it
  if (bestSplitIndex > 0 && minDistance < 10) {
    firstLine = text.substring(0, bestSplitIndex);
    secondLine = text.substring(bestSplitIndex);
  } else {
    // Fallback: try to split at underscore or space
    const spaceIndex = text.lastIndexOf("_", midPoint);
    const spaceIndex2 = text.indexOf("_", midPoint);
    const spaceIndex3 = text.lastIndexOf(" ", midPoint);
    const spaceIndex4 = text.indexOf(" ", midPoint);

    if (spaceIndex > 0 && midPoint - spaceIndex < 5) {
      firstLine = text.substring(0, spaceIndex).replace(/_/g, " ");
      secondLine = text.substring(spaceIndex + 1).replace(/_/g, " ");
    } else if (spaceIndex2 > midPoint && spaceIndex2 - midPoint < 5) {
      firstLine = text.substring(0, spaceIndex2).replace(/_/g, " ");
      secondLine = text.substring(spaceIndex2 + 1).replace(/_/g, " ");
    } else if (spaceIndex3 > 0 && midPoint - spaceIndex3 < 5) {
      firstLine = text.substring(0, spaceIndex3);
      secondLine = text.substring(spaceIndex3 + 1);
    } else if (spaceIndex4 > midPoint && spaceIndex4 - midPoint < 5) {
      firstLine = text.substring(0, spaceIndex4);
      secondLine = text.substring(spaceIndex4 + 1);
    } else {
      // Last resort: Split at midpoint if no good boundary found
      firstLine = text.substring(0, midPoint);
      secondLine = text.substring(midPoint);
    }
  }

  return { firstLine: firstLine.trim(), secondLine: secondLine.trim() };
};

// Helper function to create handles around circular node at 8 positions
const createCircularHandles = (handleType, color, nodeSize = 150) => {
  const handleCount = 8;
  const radius = nodeSize / 2;
  const handles = [];

  for (let i = 0; i < handleCount; i++) {
    // Calculate angle for each handle (8 handles at 45-degree intervals)
    // Start from top (0 degrees) and go clockwise
    const angle = (i / handleCount) * Math.PI * 2 - Math.PI / 2;
    const x = radius + radius * Math.cos(angle);
    const y = radius + radius * Math.sin(angle);

    handles.push(
      <Handle
        key={`${handleType}-${i}`}
        id={`${handleType}-${i}`}
        type={handleType}
        style={{
          background: color,
          width: 10,
          height: 10,
          border: `2px solid white`,
          left: `${x}px`,
          top: `${y}px`,
          position: "absolute",
          transform: "translate(-50%, -50%)",
          opacity: 0,
        }}
      />
    );
  }
  return handles;
};

// Custom Table Node - Professional Neo4j Style
const TableNode = ({ data }) => {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <div
      className="rounded-full border-4 border-orange-500 bg-gradient-to-br from-orange-100 to-orange-200 shadow-2xl flex items-center justify-center relative hover:shadow-3xl transition-all cursor-pointer"
      style={{ width: "150px", height: "150px" }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {createCircularHandles("target", "#fb923c", 150)}
      {createCircularHandles("source", "#fb923c", 150)}

      <div className="text-center px-3 py-2 flex flex-col items-center justify-center h-full">
        <div className="text-sm font-bold text-gray-900 leading-tight">
          {(() => {
            const { firstLine, secondLine } = splitTextForCircle(
              data.label,
              10
            );
            return (
              <>
                <div className="break-words">{firstLine}</div>
                {secondLine && (
                  <div className="break-words mt-0.5">{secondLine}</div>
                )}
              </>
            );
          })()}
        </div>
        <div className="text-xs text-gray-700 mt-1 font-semibold">
          {data.columns} cols
        </div>
      </div>

      {isHovered && data.primaryKeys && data.primaryKeys.length > 0 && (
        <div className="absolute left-1/2 -translate-x-1/2 top-full mt-4 bg-gray-900 text-white px-4 py-3 rounded-lg shadow-2xl z-50 min-w-[220px]">
          <div className="text-xs font-bold mb-2 text-yellow-300">
            🔑 Primary Keys:
          </div>
          <div className="space-y-1.5">
            {data.primaryKeys.map((pk, idx) => (
              <div key={idx} className="text-xs font-medium">
                {pk.column}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// Reference Node - Upstream/Downstream Tables
const ReferenceNode = ({ data }) => {
  const bgColor = data.isUpstream
    ? "bg-gradient-to-br from-cyan-100 to-cyan-200 border-cyan-500"
    : "bg-gradient-to-br from-green-100 to-green-200 border-green-500";

  const handleColor = data.isUpstream ? "#06b6d4" : "#10b981";

  return (
    <div
      className={`rounded-full border-3 ${bgColor} shadow-lg hover:shadow-2xl transition-all flex items-center justify-center cursor-pointer`}
      style={{ width: "120px", height: "120px" }}
    >
      {createCircularHandles("target", handleColor, 120)}
      {createCircularHandles("source", handleColor, 120)}

      <div className="text-center px-2 py-1 flex flex-col items-center justify-center h-full">
        <div className="text-xs font-bold text-gray-900 leading-tight">
          {(() => {
            const { firstLine, secondLine } = splitTextForCircle(data.label, 8);
            return (
              <>
                <div className="break-words">{firstLine}</div>
                {secondLine && (
                  <div className="break-words mt-0.5">{secondLine}</div>
                )}
              </>
            );
          })()}
        </div>
        <div className="text-xs text-gray-700 mt-1 font-semibold">
          {data.confidence}
        </div>
      </div>
    </div>
  );
};

const nodeTypes = {
  table: TableNode,
  reference: ReferenceNode,
};

// Graph View - Professional Neo4j Style with Straight Edges and Labels
const GraphView = ({ tableDetails }) => {
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);

  useEffect(() => {
    if (!tableDetails) return;

    const mainId = tableDetails.table_name;
    const newNodes = [];
    const newEdges = [];

    const centerX = 600;
    const centerY = 400;
    const upstreamRadius = 450;
    const downstreamRadius = 650;

    const parents = tableDetails.references || [];
    const children = tableDetails.referenced_by || [];

    // Main table in center
    newNodes.push({
      id: mainId,
      type: "table",
      data: {
        label: mainId,
        columns: tableDetails.column_count,
        primaryKeys: tableDetails.primary_keys || [],
      },
      position: { x: centerX, y: centerY },
    });

    // Helper function to get closest handle ID based on angle
    // Maps angle to one of 8 positions: Top, TopRight, Right, BottomRight, Bottom, BottomLeft, Left, TopLeft
    const getHandleId = (angle, handleType) => {
      // Normalize angle to 0-2π, starting from top (0 = top, going clockwise)
      let normalizedAngle = (angle + Math.PI / 2) % (Math.PI * 2);
      if (normalizedAngle < 0) normalizedAngle += Math.PI * 2;

      // Convert to 0-7 handle index (8 handles at 45-degree intervals)
      const handleIndex = Math.round((normalizedAngle / (Math.PI * 2)) * 8) % 8;
      return `${handleType}-${handleIndex}`;
    };

    // Upstream tables (references)
    parents.forEach((ref, idx) => {
      const angle = (idx / Math.max(parents.length, 1)) * Math.PI * 2;
      const uniqueParentId = `upstream-${ref.target_table}-${idx}`;

      newNodes.push({
        id: uniqueParentId,
        type: "reference",
        data: {
          label: ref.target_table,
          confidence: `${Math.round(ref.confidence * 100)}%`,
          isUpstream: true,
        },
        position: {
          x: centerX + Math.cos(angle) * upstreamRadius - 60,
          y: centerY + Math.sin(angle) * upstreamRadius - 60,
        },
      });

      // Calculate angle from parent to main node (for handle selection)
      const dx = centerX - (centerX + Math.cos(angle) * upstreamRadius - 60);
      const dy = centerY - (centerY + Math.sin(angle) * upstreamRadius - 60);
      const edgeAngle = Math.atan2(dy, dx);

      // Calculate angle from main node to parent (for target handle)
      const reverseAngle = Math.atan2(-dy, -dx);

      // Create a single edge with combined column links label
      let edgeLabel = "";
      if (ref.column_links && ref.column_links.length > 0) {
        // Combine all column links into one label
        const columnMappings = ref.column_links.map(
          (link) => `${link.source_column} → ${link.target_column}`
        );
        edgeLabel = columnMappings.join(", ");
      }

      newEdges.push({
        id: `edge-upstream-${uniqueParentId}`,
        source: uniqueParentId,
        target: mainId,
        sourceHandle: getHandleId(edgeAngle, "source"),
        targetHandle: getHandleId(reverseAngle, "target"),
        type: "straight",
        animated: false,
        style: {
          stroke: "#06b6d4",
          strokeWidth: 2.5,
        },
        markerEnd: {
          type: "arrowclosed",
          color: "#06b6d4",
          width: 20,
          height: 20,
        },
        label: edgeLabel || undefined,
        labelStyle: {
          fill: "#1f2937",
          fontWeight: 700,
          fontSize: 10,
          fontFamily: "system-ui, -apple-system, sans-serif",
        },
        labelBgStyle: {
          fill: "white",
          fillOpacity: 0.95,
        },
        labelBgPadding: [6, 6],
        labelBgBorderRadius: 4,
      });
    });

    // Downstream tables (referenced_by)
    children.forEach((ref, idx) => {
      const angle = (idx / Math.max(children.length, 1)) * Math.PI * 2;
      const uniqueChildId = `downstream-${ref.target_table}-${idx}`;

      newNodes.push({
        id: uniqueChildId,
        type: "reference",
        data: {
          label: ref.target_table,
          confidence: `${Math.round(ref.confidence * 100)}%`,
          isUpstream: false,
        },
        position: {
          x: centerX + Math.cos(angle) * downstreamRadius - 60,
          y: centerY + Math.sin(angle) * downstreamRadius - 60,
        },
      });

      // Calculate angle from main node to child (for handle selection)
      const dx = centerX + Math.cos(angle) * downstreamRadius - 60 - centerX;
      const dy = centerY + Math.sin(angle) * downstreamRadius - 60 - centerY;
      const edgeAngle = Math.atan2(dy, dx);

      // Calculate angle from child to main node (for target handle)
      const reverseAngle = Math.atan2(-dy, -dx);

      // Create a single edge with combined column links label
      let edgeLabel = "";
      if (ref.column_links && ref.column_links.length > 0) {
        // Combine all column links into one label
        const columnMappings = ref.column_links.map(
          (link) => `${link.source_column} → ${link.target_column}`
        );
        edgeLabel = columnMappings.join(", ");
      }

      newEdges.push({
        id: `edge-downstream-${uniqueChildId}`,
        source: mainId,
        target: uniqueChildId,
        sourceHandle: getHandleId(edgeAngle, "source"),
        targetHandle: getHandleId(reverseAngle, "target"),
        type: "straight",
        animated: false,
        style: {
          stroke: "#10b981",
          strokeWidth: 2.5,
        },
        markerEnd: {
          type: "arrowclosed",
          color: "#10b981",
          width: 20,
          height: 20,
        },
        label: edgeLabel || undefined,
        labelStyle: {
          fill: "#1f2937",
          fontWeight: 700,
          fontSize: 10,
          fontFamily: "system-ui, -apple-system, sans-serif",
        },
        labelBgStyle: {
          fill: "white",
          fillOpacity: 0.95,
        },
        labelBgPadding: [6, 6],
        labelBgBorderRadius: 4,
      });
    });

    setNodes(newNodes);
    setEdges(newEdges);
  }, [tableDetails, setNodes, setEdges]);

  return (
    <div className="flex-1 flex flex-col overflow-hidden relative bg-gradient-to-br from-gray-50 to-gray-100">
      {/* Professional Legend */}
      <div className="absolute top-6 left-6 bg-white shadow-xl rounded-lg p-4 z-50 border-2 border-gray-200">
        <h4 className="font-bold text-gray-900 text-sm mb-3 border-b pb-2">
          Legend
        </h4>

        <div className="space-y-2.5">
          <div className="flex items-center gap-3">
            <div className="w-5 h-5 rounded-full border-3 border-orange-500 bg-orange-100 flex-shrink-0"></div>
            <span className="text-xs font-semibold text-gray-700">
              Main Table
            </span>
          </div>

          <div className="flex items-center gap-3">
            <div className="w-5 h-5 rounded-full border-3 border-cyan-500 bg-cyan-100 flex-shrink-0"></div>
            <span className="text-xs font-semibold text-gray-700">
              Upstream (Referenced)
            </span>
          </div>

          <div className="flex items-center gap-3">
            <div className="w-5 h-5 rounded-full border-3 border-green-500 bg-green-100 flex-shrink-0"></div>
            <span className="text-xs font-semibold text-gray-700">
              Downstream (References This)
            </span>
          </div>
        </div>
      </div>

      <ReactFlow
        nodes={nodes}
        edges={edges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        nodeTypes={nodeTypes}
        fitView
        fitViewOptions={{ padding: 0.3, minZoom: 0.4, maxZoom: 1.5 }}
        defaultViewport={{ x: 0, y: 0, zoom: 0.75 }}
        minZoom={0.2}
        maxZoom={2}
        connectionLineType="straight"
        defaultEdgeOptions={{
          type: "straight",
          animated: false,
        }}
      >
        <Background color="#d1d5db" gap={20} size={1.5} variant="dots" />
        <Controls position="top-right" showInteractive={false} />
        {/* <MiniMap
          nodeColor={(node) => {
            if (node.type === "table") return "#fb923c";
            return node.data.isUpstream ? "#06b6d4" : "#10b981";
          }}
          maskColor="rgba(0, 0, 0, 0.1)"
          position="bottom-right"
          style={{
            height: 120,
            width: 150,
            backgroundColor: "white",
            border: "2px solid #e5e7eb",
            borderRadius: "8px",
          }}
        /> */}
      </ReactFlow>
    </div>
  );
};

export default DataLineageDashboard;
